import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { ScrollFadeIn } from "@/components/effects/ScrollFadeIn";
import { useAuth } from "@/contexts/useAuth";

const plans = [
  {
    name: "Sprints",
    subtitle: "Ongoing Product Delivery",
    description: "For founders who need steady execution. We plan, design, and build in focused 2-week cycles around the highest-priority work.",
    price: "$4,500",
    period: "/ 2 weeks",
    note: "Pause or cancel between cycles",
    features: [
      "Senior product and engineering execution",
      "Sprint planning and roadmap guidance",
      "Frontend, backend, and API delivery",
      "AI, automation, and third-party integrations",
      "Async collaboration through your client portal",
      "Weekly review and next-sprint alignment",
    ],
    cta: "Book a Call",
    featured: false,
    style: "angular",
  },
  {
    name: "MVP Development",
    subtitle: "Tight-Scope Launch Build",
    description: "For founders who need a credible v1 fast. We take a clearly defined scope from concept to a launch-ready MVP in 2 weeks.",
    price: "$9,500",
    period: "/ fixed project",
    note: "Best for well-defined MVP scopes",
    features: [
      "End-to-end MVP built in a focused 2-week window",
      "Core user flows, auth, and onboarding",
      "Database, API, and third-party integrations",
      "Responsive UI and design system foundation",
      "Deployment-ready build and handoff",
      "Weekly review and launch planning",
    ],
    cta: "Get Started",
    featured: true,
    style: "glowing",
  },
  {
    name: "Enterprise / Complex Builds",
    subtitle: "Custom Scope, Custom Pricing",
    description: "For larger platforms, migrations, multi-system workflows, or high-stakes builds that need deeper planning, phased delivery, and a tailored commercial structure.",
    price: "Custom",
    period: "/ scoped engagement",
    note: "Quoted after discovery and technical review",
    features: [
      "Custom statement of work and delivery plan",
      "Architecture for scale, security, and reliability",
      "Legacy migrations and complex integrations",
      "Multi-workstream execution across product and engineering",
      "Milestone-based roadmap and stakeholder alignment",
      "Dedicated communication for longer engagements",
    ],
    cta: "Request Proposal",
    featured: false,
    style: "angular",
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAccessPortal = !!user && (user.isAdmin || user.isProvisionedClient);

  const handleBookCall = () => {
    if (canAccessPortal) {
      navigate("/portal");
    } else {
      navigate("/contact");
    }
  };

  return (
    <section id="pricing" className="py-28 px-4 relative">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background to-transparent" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <ScrollFadeIn>
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 font-heading">
              Simple, <span className="text-gradient-indigo">transparent</span> pricing
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start with a productized engagement or scope a larger custom build. Clear pricing up front, with larger projects quoted separately.
            </p>
          </div>
        </ScrollFadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan, index) => {
            return (
              <ScrollFadeIn key={index} delay={index * 150}>
                <div
                  className={`
                    group relative rounded-3xl p-8 sm:p-10 transition-all duration-500 transform-gpu h-full
                    ${plan.featured
                      ? "glass-card glow-border-gold hover:shadow-glow-gold"
                      : "glass-card glow-border hover-lift-glow"
                    }
                  `}
                  style={{ willChange: 'transform, box-shadow' }}
              >
                {/* Featured badge */}
                {plan.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[hsl(var(--gold))] to-[hsl(var(--gold-glow))] text-background px-5 py-1.5 rounded-full text-sm font-semibold shadow-glow-gold">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-bold text-xl mb-1 font-heading">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {plan.subtitle}
                  </p>
                </div>

                <p className="text-sm mb-8 text-muted-foreground">
                  {plan.description}
                </p>

                <div className="mb-2">
                  <span className={`
                    text-5xl font-bold font-mono tracking-tight
                    ${plan.featured ? "text-gradient-gold" : "text-gradient-indigo"}
                  `}>
                    {plan.price}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">
                    {plan.period}
                  </span>
                </div>
                <p className="text-sm mb-8 text-muted-foreground/70">
                  {plan.note}
                </p>

                <Button
                  variant="outline"
                  size="lg"
                  className={`w-full mb-8 gap-2 ${plan.featured ? "border-[hsl(var(--gold)/0.3)] hover:border-[hsl(var(--gold)/0.5)] hover:bg-[hsl(var(--gold)/0.05)]" : "border-foreground/20 hover:bg-foreground/5"}`}
                  onClick={handleBookCall}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4" />
                </Button>

                <div>
                  <p className="text-sm font-semibold mb-5">
                    What's included:
                  </p>
                  <ul className="space-y-4">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div className={`
                          w-5 h-5 rounded-full flex items-center justify-center transition-colors
                          ${plan.featured 
                            ? "bg-[hsl(var(--gold)/0.15)]" 
                            : "bg-primary/10"
                          }
                        `}>
                          <Check className={`w-3 h-3 ${plan.featured ? "text-[hsl(var(--gold))]" : "text-primary"}`} />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                </div>
              </ScrollFadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
